import React, { useEffect, useState } from "react";
import TableColumn from "../../components/TableColumn/TableColumn";
import api from "../../services/api";
import "./PromoterRequests.scss";
import { format } from "date-fns";
import SideBar from "../../components/SideBar/SideBar";
import TopNav from "../../components/TopNav/TopNav";
import profileDefault from "../../assets/images/profileDefault.png";
import Button from "../../components/Buttons/Button";
import AddVerifiedModal from "../../components/AddVerifiedModal/AddVerifiedModal";

// Self-serve "Verified Promoter" requests. Same shape as DJ Requests on
// purpose: promoters are venue-independent too, so approve/deny is inline with
// no venue to assign. Approve flips the request to Verified Promoter on the
// server, which is what unlocks the tag and plan creation.
const PromoterRequests = () => {
  const [rows, setRows] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const records = 10;
  const [adding, setAdding] = useState(false);

  const formatDate = (d) => (d ? format(new Date(d), "MMMM d, yyyy") : "-");

  const igCell = (handle) =>
    handle ? (
      <a
        href={`https://instagram.com/${handle}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "#4F30EA", textDecoration: "underline" }}
      >
        @{handle}
      </a>
    ) : (
      "-"
    );

  const handleReview = async (id, action, mode = "") => {
    let reason = "";
    if (action === "disapprove") {
      const q =
        mode === "remove"
          ? "Remove this promoter? They'll lose the Verified Promoter tag and plan access. Optional reason:"
          : "Deny this promoter request? You can add an optional reason:";
      reason = prompt(q);
      if (reason === null) return; // cancelled
    } else if (
      !window.confirm(
        "Approve this promoter? They'll get the Verified Promoter tag and can create plans."
      )
    ) {
      return;
    }
    try {
      await api.post(`/promoter-requests/review/${id}`, { action, reason: reason || "" });
      getRequests();
    } catch (error) {
      console.error("Error processing promoter request ==>", error);
      alert("Error processing request");
    }
  };

  const selectStyle = {
    background: "rgba(255, 255, 255, 0.06)",
    color: "#fff",
    border: "1px solid rgba(255, 255, 255, 0.15)",
    borderRadius: 6,
    padding: "6px 10px",
    fontSize: 12,
    fontWeight: 500,
    cursor: "pointer",
    outline: "none",
  };

  // "Active" = Approved AND flagged promoter_active. An Approved row without
  // the flag was removed later and reads as Removed, same as the DJ tab.
  const statusOf = (r) =>
    r.review_status === "Pending"
      ? "Pending"
      : r.review_status === "Approved" && r.promoter_active === true
      ? "Approved"
      : "Removed";

  const toRow = (r) => {
    const status = statusOf(r);
    const isActive = status === "Approved";
    const isPending = status === "Pending";
    return {
      Photo: (
        <img
          className="rounded"
          style={{ height: 40, width: 40, objectFit: "cover", borderRadius: 999 }}
          src={r.photo_url || profileDefault}
          alt={r.name || "Promoter"}
        />
      ),
      Name: r.name,
      Instagram: igCell(r.instagram),
      Team: r.company || "-",
      Works: r.promoter_venues && r.promoter_venues.length ? r.promoter_venues.join(", ") : "-",
      "Date Created": formatDate(r.created_at),
      Status: status,
      Actions: (
        <select
          defaultValue=""
          onChange={(e) => {
            const action = e.target.value;
            if (action === "accept") handleReview(r._id, "approve");
            else if (action === "deny") handleReview(r._id, "disapprove", "deny");
            else if (action === "remove") handleReview(r._id, "disapprove", "remove");
            e.target.value = "";
          }}
          style={selectStyle}
        >
          <option value="" disabled style={{ color: "#000" }}>Action ▾</option>
          {isPending && <option value="accept" style={{ color: "#000" }}>Accept</option>}
          {isPending && <option value="deny" style={{ color: "#000" }}>Deny</option>}
          {isActive && <option value="remove" style={{ color: "#000" }}>Remove</option>}
          {!isPending && !isActive && <option value="accept" style={{ color: "#000" }}>Re-approve</option>}
        </select>
      ),
      id: r._id,
    };
  };

  const getRequests = async () => {
    try {
      const response = await api.get(`/promoter-requests`);
      const data = response.data?.data || [];
      setRows(data.map(toRow));
      setTotalPages(1);
    } catch (error) {
      console.error(`Error getting promoter requests. Error: ${error}`);
    }
  };

  useEffect(() => {
    getRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAllData = async () => {
    try {
      const response = await api.get(`/promoter-requests`);
      const data = response.data?.data || [];
      return data.map((r) => ({
        Name: r.name,
        Instagram: r.instagram ? `@${r.instagram}` : "-",
        Team: r.company || "-",
        Works: r.promoter_venues && r.promoter_venues.length ? r.promoter_venues.join(", ") : "-",
        Photo: r.photo_url || "",
        "Date Created": formatDate(r.created_at),
        Status: statusOf(r),
        id: r._id,
      }));
    } catch (error) {
      console.log(`Error fetching all promoter requests: ${error}`);
      return [];
    }
  };

  const columns = ["Photo", "Name", "Instagram", "Team", "Works", "Date Created", "Status", "Actions"];

  const handleChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => setCurrentPage(page);

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content">
          <section className="users">
            <TableColumn
              title="Promoter Requests"
              hasHeader={true}
              columns={columns}
              data={rows}
              sortableColumns={[]}
              hasExport={true}
              hasSearch={true}
              totalPages={totalPages}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              records={records}
              inputSearchProps={{ value: searchTerm, onChange: handleChange }}
              fetchAllData={fetchAllData}
              customButton={
                <Button className="btn-outline-light" onClick={() => setAdding(true)}>
                  + Add Promoter
                </Button>
              }
              onSort={() => {}}
            />
          </section>
          <AddVerifiedModal
            isOpen={adding}
            kind="promoter"
            onClose={() => setAdding(false)}
            onAdded={(message) => {
              setAdding(false);
              getRequests();
              alert(message);
            }}
          />
        </article>
      </div>
    </main>
  );
};

export default PromoterRequests;
